import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';

import add from '../helpers/add.ts';
import sub from '../helpers/sub.ts';

interface PaginationByPageNumberSignature {
  Element: HTMLDivElement;
  Args: {
    pageIndex: number;
    pageSize: number;
    total: number;
    onPageIndexChange: (pageIndex: number) => void;
  };
}

interface PaginationByOffsetSignature {
  Element: HTMLDivElement;
  Args: {
    offset: number;
    limit: number;
    total: number;
    onOffsetChange: (offset: number) => void;
  };
}

function eq(a: unknown, b: unknown): boolean {
  return a === b;
}

function gt(a: number, b: number): boolean {
  return a > b;
}

abstract class PaginationBase<A> extends Component<A> {
  abstract get total(): number;
  abstract get pageSize(): number;

  /** The index of the page. (Starts at 0) */
  abstract get pageIndex(): number;

  /** The user-facing number of the page. (Starts at 1) */
  abstract get pageDisplayNumber(): number;

  get totalPages(): number {
    if (this.pageSize <= 0) {
      return 0;
    }
    return Math.ceil(this.total / this.pageSize);
  }

  get visiblePages(): number[] {
    const current = this.pageDisplayNumber;
    const total = this.totalPages;
    const pages: number[] = [];

    if (total <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (current <= 3) {
        pages.push(2, 3, 4);
        pages.push(-1); // ellipsis
        pages.push(total);
      } else if (current >= total - 2) {
        pages.push(-1); // ellipsis
        pages.push(total - 3, total - 2, total - 1, total);
      } else {
        pages.push(-1); // ellipsis
        pages.push(current - 1, current, current + 1);
        pages.push(-2); // ellipsis
        pages.push(total);
      }
    }

    return pages;
  }

  /** Sets the index of the page. (Starts at 0) */
  abstract setPageIndex: (pageIndex: number) => void;

  /** Sets the user-facing number of the page. (Starts at 1) */
  abstract setPageDisplayNumber: (pageDisplayNumber: number) => void;

  <template>
    {{#if (gt this.totalPages 1)}}
      <nav aria-label="Pagination" ...attributes>
        <ul class="pagination pagination-sm mb-0">
          {{! Previous button }}
          <li
            class="page-item {{if (eq this.pageDisplayNumber 1) 'disabled' ''}}"
          >
            <button
              type="button"
              class="page-link px-3"
              disabled={{eq this.pageDisplayNumber 1}}
              aria-label="Previous page"
              {{on
                "click"
                (fn this.setPageDisplayNumber (sub this.pageDisplayNumber 1))
              }}
            >
              <iconify-icon inline icon="ph:arrow-left" />
              Previous
            </button>
          </li>

          {{! Page numbers with ellipsis }}
          {{#each this.visiblePages as |pageNum|}}
            {{#if (eq pageNum -1)}}
              <li class="page-item disabled">
                <span class="page-link px-3">…</span>
              </li>
            {{else if (eq pageNum -2)}}
              <li class="page-item disabled">
                <span class="page-link px-3">…</span>
              </li>
            {{else}}
              <li
                class="page-item
                  {{if (eq this.pageDisplayNumber pageNum) 'active' ''}}"
              >
                <button
                  type="button"
                  class="page-link px-3"
                  aria-label="Page {{pageNum}}"
                  {{on "click" (fn this.setPageDisplayNumber pageNum)}}
                >
                  {{pageNum}}
                </button>
              </li>
            {{/if}}
          {{/each}}

          {{! Next button }}
          <li
            class="page-item
              {{if (eq this.pageDisplayNumber this.totalPages) 'disabled' ''}}"
          >
            <button
              type="button"
              class="page-link px-3"
              disabled={{eq this.pageDisplayNumber this.totalPages}}
              aria-label="Next page"
              {{on
                "click"
                (fn this.setPageDisplayNumber (add this.pageDisplayNumber 1))
              }}
            >
              Next
              <iconify-icon inline icon="ph:arrow-right" />
            </button>
          </li>
        </ul>
      </nav>
    {{/if}}
  </template>
}

export class PaginationByPageNumber extends PaginationBase<PaginationByPageNumberSignature> {
  get total() {
    return this.args.total;
  }

  get pageSize() {
    return this.args.pageSize;
  }

  get pageDisplayNumber() {
    return this.args.pageIndex + 1;
  }

  get pageIndex() {
    return this.args.pageIndex;
  }

  setPageIndex = (pageIndex: number): void => {
    this.args.onPageIndexChange(pageIndex);
  };

  setPageDisplayNumber = (pageDisplayNumber: number): void => {
    this.args.onPageIndexChange(pageDisplayNumber - 1);
  };
}

export class PaginationByOffset extends PaginationBase<PaginationByOffsetSignature> {
  get total() {
    return this.args.total;
  }

  get pageSize() {
    return this.args.limit;
  }

  get pageIndex() {
    const limit = this.args.limit;

    // Guard against zero, negative, or invalid limits to avoid division by zero.
    if (typeof limit !== 'number' || limit <= 0) {
      return 0;
    }

    return Math.floor(this.args.offset / limit);
  }

  get pageDisplayNumber() {
    return this.pageIndex + 1;
  }

  setPageIndex = (pageIndex: number): void => {
    this.args.onOffsetChange(pageIndex * this.args.limit);
  };

  setPageDisplayNumber = (pageDisplayNumber: number): void => {
    this.args.onOffsetChange((pageDisplayNumber - 1) * this.args.limit);
  };
}
